import React, { useEffect, useMemo, useState } from 'react';
import { alpha, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import { BackendNode, GraphData, KnowledgeGraphPanelProps } from './types';
import { getBgColor } from './layout';
import { satelliteColor } from './nodeTypeColors';
import OverviewSvg from './OverviewSvg';
import FocusSvg from './FocusSvg';
import ReportLensSelect from './ReportLensSelect';

export default function KnowledgeGraphPanel({
    dataUrl,
    currentNodeId,
    onNodeSelect,
    reports,
    selectedReportElementId,
    onReportLensChange,
    reportMemberUids,
    onFocusedNodeChange,
    relatedObjectUids,
}: KnowledgeGraphPanelProps) {
    const { t } = useBundleTranslation('components/common/knowledge-graph/panel');
    const theme = useTheme();
    const borderColor = alpha(theme.palette.text.primary, 0.08);
    const bgColor = getBgColor(theme);
    const [focusedNodeId, setFocusedNodeId] = useState<number | null>(null);
    const [hoveredId, setHoveredId] = useState<number | null>(null);
    const [localReportLensId, setLocalReportLensId] = useState<number | null>(null);

    const { data, isLoading, isError } = useQuery<GraphData>({
        queryKey: ['knowledge-graph', 'panel-data', dataUrl],
        queryFn: () => instance.get(dataUrl).then((r) => r.data),
    });

    const { hostNode, routedFocusNode, leftNodes, rightNodes, typeColors } = useMemo(() => {
        const empty = {
            hostNode: null as BackendNode | null,
            routedFocusNode: null as BackendNode | null,
            leftNodes: [] as BackendNode[],
            rightNodes: [] as BackendNode[],
            typeColors: new Map<string, string>(),
        };
        if (!data) return empty;

        // Host = the payload's root-flagged node (backend NodeRole authority, MI-30169); the
        // first-node fallback covers a subgraph with no root-flagged node.
        const host = data.nodes.find((n) => n.is_root) ?? data.nodes[0];

        const routedFocus =
            currentNodeId != null
                ? (data.nodes.find(
                      (n) =>
                          n.llm_learning_node_id === currentNodeId &&
                          n.llm_learning_node_id !== host.llm_learning_node_id,
                  ) ?? null)
                : null;

        const satellites = data.nodes.filter((n) => n.llm_learning_node_id !== host.llm_learning_node_id);

        const byType = new Map<string, BackendNode[]>();
        for (const n of satellites) {
            const key = n.type; // canonical type, not n.node_type_name
            if (!byType.has(key)) byType.set(key, []);
            byType.get(key)!.push(n);
        }
        const groups = [...byType.values()];
        const leftNodes = groups.filter((_, i) => i % 2 === 0).flat();
        const rightNodes = groups.filter((_, i) => i % 2 === 1).flat();

        // Host first (its own DB-driven color, same dot the user sees at the center of Overview),
        // then each satellite type's canonical color — so the legend always names every dot the
        // user can actually see on this graph, not just the satellite types.
        const typeColors = new Map<string, string>();
        typeColors.set(host.type, host.node_color ?? '#CE1F6C');
        satellites.forEach((n) => {
            if (!typeColors.has(n.type)) typeColors.set(n.type, satelliteColor(n.type, n.node_color));
        });

        return { hostNode: host, routedFocusNode: routedFocus, leftNodes, rightNodes, typeColors };
    }, [data, currentNodeId]);

    const focusedNode =
        currentNodeId != null
            ? routedFocusNode
            : (data?.nodes.find((n) => n.llm_learning_node_id === focusedNodeId) ?? null);

    const mode: 'overview' | 'focus' = focusedNode ? 'focus' : 'overview';

    // Fires on every render where focusedNode's identity changed, including the first — a
    // click-handler-only version would miss initial route-driven focus (deep link, back button).
    useEffect(() => {
        onFocusedNodeChange?.(focusedNode);
    }, [focusedNode, onFocusedNodeChange]);

    const relatedNodes = useMemo(() => {
        if (!focusedNode || !hostNode || !data) return [];

        const focusedUid = focusedNode.object_uid;
        if (!focusedUid) return [];

        const uidToNode = new Map<string, BackendNode>();
        data.nodes.forEach((n) => {
            if (n.object_uid) uidToNode.set(n.object_uid, n);
        });

        // Real Measure<->Date usage relationships (§7's actual fan-out) never appear in `data.edges`
        // — verified live, that array only ever carries satellite->model membership edges, for any
        // model. The caller (when it has the real usage data — modelAction's measure_time_behavior)
        // passes the related object_uids directly; this is the only path that ever finds anything
        // for a Measure/Date focus. Fall back to the edge-based scan only when the caller hasn't
        // supplied one, so standalone/sandbox usage (no relatedObjectUids prop) keeps working.
        if (relatedObjectUids) {
            return relatedObjectUids
                .map((uid) => uidToNode.get(uid))
                .filter(
                    (n): n is BackendNode =>
                        n !== undefined &&
                        n.llm_learning_node_id !== hostNode.llm_learning_node_id &&
                        n.llm_learning_node_id !== focusedNode.llm_learning_node_id,
                );
        }

        const connectedUids = new Set<string>();
        data.edges.forEach((e) => {
            if (e.source_node_uid === focusedUid) connectedUids.add(e.target_node_uid);
            if (e.target_node_uid === focusedUid) connectedUids.add(e.source_node_uid);
        });

        const neighbors = [...connectedUids]
            .map((uid) => uidToNode.get(uid))
            .filter(
                (n): n is BackendNode =>
                    n !== undefined &&
                    n.llm_learning_node_id !== hostNode.llm_learning_node_id &&
                    n.llm_learning_node_id !== focusedNode.llm_learning_node_id,
            );

        // §7: focusing a Date shows only the measures that compute across it (never other
        // dates or dimensions); focusing a Measure shows its dimensions and dates.
        if (focusedNode.type === 'Date') {
            return neighbors.filter((n) => n.type === 'Measure');
        }
        if (focusedNode.type === 'Measure') {
            return neighbors.filter((n) => n.type === 'Dimension' || n.type === 'Date');
        }
        return neighbors;
    }, [focusedNode, hostNode, data, relatedObjectUids]);

    const handleNodeClick = (node: BackendNode) => {
        if (!hostNode || node.llm_learning_node_id === hostNode.llm_learning_node_id) {
            onNodeSelect?.(node);
            return;
        }

        if (onNodeSelect) {
            onNodeSelect(node);
        } else {
            setFocusedNodeId(node.llm_learning_node_id);
        }
    };

    const handleRelatedClick = (node: BackendNode) => {
        if (onNodeSelect) {
            onNodeSelect(node);
        } else {
            setFocusedNodeId(node.llm_learning_node_id);
        }
    };

    const backToOverview = () => {
        if (hostNode && onNodeSelect) {
            onNodeSelect(hostNode);
        } else {
            setFocusedNodeId(null);
        }
        setHoveredId(null);
    };

    const reportLensId = selectedReportElementId !== undefined ? selectedReportElementId : localReportLensId;
    const reportLensActive = reportLensId !== null;

    const handleReportLensChange = (elementId: number | null) => {
        if (onReportLensChange) {
            onReportLensChange(elementId);
        } else {
            setLocalReportLensId(elementId);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <CircularProgress size={36} />
            </Box>
        );
    }

    if (isError || !hostNode) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <Typography color="error">{t('load_error')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    flexShrink: 0,
                    px: 2,
                    minHeight: 46,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    borderBottom: `1px solid ${borderColor}`,
                }}
            >
                {mode === 'overview' ? (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 15 }}>
                            {t('title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('overview_subtitle')}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 15 }}>
                            {t('focused_title', { name: focusedNode?.name })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('focus_subtitle')}{' '}
                            <Box
                                component="a"
                                onClick={backToOverview}
                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                            >
                                {t('back_to_overview_link')}
                            </Box>
                        </Typography>
                    </>
                )}
                {reports && reports.length > 0 && (
                    <ReportLensSelect
                        reports={reports}
                        selectedReportElementId={reportLensId}
                        onChange={handleReportLensChange}
                    />
                )}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                <Box sx={{ bgcolor: bgColor, borderRadius: 1, p: 1, position: 'relative' }}>
                    {mode === 'overview' && (
                        <OverviewSvg
                            centerNode={hostNode}
                            leftNodes={leftNodes}
                            rightNodes={rightNodes}
                            typeColors={typeColors}
                            hoveredId={hoveredId}
                            reportLensActive={reportLensActive}
                            reportMemberUids={reportMemberUids}
                            onHover={setHoveredId}
                            onClick={handleNodeClick}
                        />
                    )}
                    {mode === 'focus' && focusedNode && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                data-test="kg_graph_back_to_overview"
                                onClick={backToOverview}
                                startIcon={<IconMi icon="chevron-left" />}
                                sx={{
                                    position: 'absolute',
                                    top: 14,
                                    left: 14,
                                    zIndex: 1,
                                    bgcolor: 'white',
                                    fontSize: 12,
                                }}
                            >
                                {t('overview_button')}
                            </Button>
                            <FocusSvg
                                centerNode={hostNode}
                                focusedNode={focusedNode}
                                relatedNodes={relatedNodes}
                                onClickCenter={backToOverview}
                                typeColors={typeColors}
                                hoveredId={hoveredId}
                                reportLensActive={reportLensActive}
                                reportMemberUids={reportMemberUids}
                                onHover={setHoveredId}
                                onClickRelated={handleRelatedClick}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
